import { createApp, defineAsyncComponent } from "vue";

// Vue external scripts
import store from "./store/index.js"; // VueX Store Import
import router from "./router/index.js"; // Vue Router Import

// Vue UI Components
import TopNav from "./components/layout/TheTopNavigation.vue";

// Primevue themes
import "primevue/resources/themes/mdc-light-deeppurple/theme.css"; // theme
import "primeicons/primeicons.css"; // icons
import "primevue/resources/primevue.min.css"; // core css
import "@/assets/css/global_style.css"; // app global css
import Passthrough from "./util/primevuePassthrough.js"; // Primevue passthrough css

// Primevue components
import PrimeVue from "primevue/config";
import InputText from "primevue/inputtext";
import Textarea from "primevue/textarea";
import InputNumber from "primevue/inputnumber";
import Dropdown from "primevue/dropdown";
import AutoComplete from "primevue/autocomplete";
import InputSwitch from "primevue/inputswitch";
import Button from "primevue/button";
import Tooltip from "primevue/tooltip";
import OverlayPanel from "primevue/overlaypanel";
// PrimeVue 4 renames this to Drawer; on 3.44 the same component is Sidebar.
import SideDrawer from "primevue/sidebar";
import DataTable from "primevue/datatable";
import TableColumn from "primevue/column";
import ToastMessage from "primevue/toast";
import ToastService from "primevue/toastservice";
import SelectButton from "primevue/selectbutton";
import TabMenu from "primevue/tabmenu";
import StandardDialog from "primevue/dialog";
import DynamicDialog from "primevue/dynamicdialog";
import DialogService from "primevue/dialogservice";
import Checkbox from "primevue/checkbox";
import Badge from "primevue/badge";
import Menu from "primevue/menu";
import Skeleton from "primevue/skeleton";
import SplitButton from "primevue/splitbutton";
import ProgressBar from "primevue/progressbar";
import ProgressSpinner from "primevue/progressspinner";
import FileUpload from "primevue/fileupload";
import InlineMessage from "primevue/message";
import StatusTag from "primevue/tag";
import ProfileImage from "primevue/avatar";
import Galleria from "primevue/galleria";
import MultiSelect from "primevue/multiselect";
import RadioButton from "primevue/radiobutton";
import Chip from "primevue/chip";

const Steps = defineAsyncComponent(() => import("primevue/steps"));
const Calendar = defineAsyncComponent(() => import("primevue/calendar"));

//Third Party Applications
import { createAuth0 } from "@auth0/auth0-vue"; // Auth0 - Authentication
import { createI18n } from "vue-i18n"; // I18n - Crowdin Translations
import { languages } from "./locales/index.js";

// Create Application
import App from "@/App";

const app = createApp(App);

app.use(PrimeVue, Passthrough);
app.use(ToastService);
app.use(DialogService);
app.use(router);
app.use(store);

// Third party
app.use(
  // Auth0
  createAuth0({
    domain: process.env.VUE_APP_AUTH0_CONFIG_DOMAIN,
    clientId: process.env.VUE_APP_AUTH0_CONFIG_CLIENTID,
    authorizationParams: {
      redirect_uri: process.env.VUE_APP_AUTH0_CONFIG_CALLBACK_URL,
      audience: process.env.VUE_APP_AUTH0_API_IDENTIFIER,
    },
  })
);
app.use(
  createI18n({
    // I18n
    locale: "en",
    fallbackLocale: "en",
    messages: languages,
  })
);

app.component("TopNav", TopNav);

app.component("InputText", InputText);
app.component("TextArea", Textarea);
app.component("InputNumber", InputNumber);
app.component("DropDown", Dropdown);
app.component("AutoCompleteInput", AutoComplete);
app.component("InputSwitch", InputSwitch);
app.component("AppButton", Button);
app.component("SplitButton", SplitButton);
app.component("OverlayPanel", OverlayPanel);
app.component("SideDrawer", SideDrawer);
app.component("DataTable", DataTable);
app.component("TableColumn", TableColumn);
app.component("ToastMessage", ToastMessage);
app.component("SelectButton", SelectButton);
app.component("TabMenu", TabMenu);
app.component("StandardDialog", StandardDialog);
app.component("DynamicDialog", DynamicDialog);
app.component("CalendarPicker", Calendar);
app.component("CheckBox", Checkbox);
app.component("ItemBadge", Badge);
app.component("FormSteps", Steps);
app.component("MenuButton", Menu);
app.component("LoadingSkeleton", Skeleton);
app.component("ProgressBar", ProgressBar);
app.component("ProgressSpinner", ProgressSpinner);
app.component("FileUpload", FileUpload);
app.component("InlineMessage", InlineMessage);
app.component("StatusTag", StatusTag);
app.component("ProfileImage", ProfileImage);
app.component("GalleryViewer", Galleria);
app.component("MultiSelect", MultiSelect);
app.component("ChipItem", Chip);
app.component("RadioButton", RadioButton);

app.directive("tooltip", Tooltip);
app.mount("#app");
